import { useEffect, useState } from "react";

type ConsentState = {
  necessary: true;
  analytics: boolean;
  marketing: boolean;
};

const STORAGE_KEY = "auria_cookie_consent";

function loadConsent(): ConsentState | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? (JSON.parse(raw) as ConsentState) : null;
  } catch {
    return null;
  }
}

function saveConsent(consent: ConsentState) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(consent));
  } catch {
    // ignore
  }
}

export default function CookieBanner() {
  const [visible, setVisible] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const [analytics, setAnalytics] = useState(true);
  const [marketing, setMarketing] = useState(false);

  useEffect(() => {
    const existing = loadConsent();
    if (!existing) {
      const timer = setTimeout(() => setVisible(true), 800);
      return () => clearTimeout(timer);
    }
  }, []);

  const accept = (type: "all" | "necessary" | "custom") => {
    const consent: ConsentState = {
      necessary: true,
      analytics: type === "all" ? true : type === "necessary" ? false : analytics,
      marketing: type === "all" ? true : type === "necessary" ? false : marketing,
    };
    saveConsent(consent);
    setVisible(false);
  };

  if (!visible) return null;

  return (
    <div
      className="fixed bottom-0 left-0 right-0 z-50 p-4 md:p-6"
      role="dialog"
      aria-label="Aviso de cookies"
      aria-modal="false"
    >
      <div
        className="max-w-4xl mx-auto rounded-2xl border border-stone-200 bg-white"
        style={{ boxShadow: "0 -4px 40px rgba(0,0,0,0.08)" }}
      >
        {/* Main row */}
        <div className="p-5 md:p-6">
          <div className="flex flex-col md:flex-row md:items-start gap-5">
            {/* Icon + text */}
            <div className="flex items-start gap-4 flex-1">
              <div className="w-10 h-10 flex items-center justify-center flex-shrink-0 rounded-xl bg-amber-50">
                <i className="ri-shield-check-line text-amber-600 text-lg" />
              </div>
              <div>
                <h3 className="text-stone-900 text-sm font-bold mb-1">
                  Tu privacidad importa
                </h3>
                <p className="text-stone-500 text-xs leading-relaxed max-w-xl">
                  Usamos cookies para garantizar el funcionamiento de la plataforma y, con tu permiso, mejorar la experiencia y mostrarte contenido relevante. Puedes{" "}
                  <button
                    onClick={() => setExpanded((v) => !v)}
                    className="text-amber-600 font-semibold hover:underline cursor-pointer"
                  >
                    gestionar tus preferencias
                  </button>{" "}
                  o consultar nuestra{" "}
                  <a href="#" rel="nofollow" className="text-amber-600 font-semibold hover:underline">
                    Política de Privacidad
                  </a>
                  .
                </p>
              </div>
            </div>

            {/* Buttons */}
            <div className="flex flex-row md:flex-col gap-2 flex-shrink-0">
              <button
                onClick={() => accept("all")}
                className="px-5 py-2.5 bg-amber-500 hover:bg-amber-400 text-white text-xs font-bold rounded-xl transition-colors cursor-pointer whitespace-nowrap"
              >
                Aceptar todas
              </button>
              <button
                onClick={() => accept("necessary")}
                className="px-5 py-2.5 border border-stone-200 hover:border-stone-300 text-stone-700 text-xs font-semibold rounded-xl transition-colors cursor-pointer whitespace-nowrap"
              >
                Solo necesarias
              </button>
            </div>
          </div>

          {/* Expandable preferences */}
          {expanded && (
            <div className="mt-5 pt-5 border-t border-stone-100 grid sm:grid-cols-3 gap-4">
              {/* Necessary */}
              <CookieCategory
                icon="ri-lock-2-line"
                title="Necesarias"
                description="Esenciales para el funcionamiento del sitio, el checkout de Stripe y la seguridad de tu sesión."
                enabled={true}
                locked
                onChange={() => {}}
              />
              {/* Analytics */}
              <CookieCategory
                icon="ri-bar-chart-line"
                title="Analíticas"
                description="Nos ayudan a entender cómo usas Auria para mejorar el producto. Ningún dato se vende."
                enabled={analytics}
                onChange={setAnalytics}
              />
              {/* Marketing */}
              <CookieCategory
                icon="ri-megaphone-line"
                title="Marketing"
                description="Usadas para mostrarte contenido personalizado en otras plataformas. Totalmente opcionales."
                enabled={marketing}
                onChange={setMarketing}
              />
            </div>
          )}

          {expanded && (
            <div className="mt-4 flex items-center justify-between">
              <p className="text-stone-400 text-xs">
                <i className="ri-eu-line mr-1" />
                Cumplimiento RGPD · Datos almacenados en la UE · ISO 27001
              </p>
              <button
                onClick={() => accept("custom")}
                className="px-5 py-2.5 bg-stone-900 hover:bg-stone-800 text-white text-xs font-bold rounded-xl transition-colors cursor-pointer whitespace-nowrap"
              >
                Guardar preferencias
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

interface CookieCategoryProps {
  icon: string;
  title: string;
  description: string;
  enabled: boolean;
  locked?: boolean;
  onChange: (v: boolean) => void;
}

function CookieCategory({ icon, title, description, enabled, locked, onChange }: CookieCategoryProps) {
  return (
    <div className="flex flex-col gap-2 p-3.5 rounded-xl bg-stone-50 border border-stone-100">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <div className="w-6 h-6 flex items-center justify-center">
            <i className={`${icon} text-stone-600 text-sm`} />
          </div>
          <span className="text-stone-800 text-xs font-bold">{title}</span>
        </div>
        {locked ? (
          <span className="text-xs text-stone-400 font-medium px-2 py-0.5 bg-stone-100 rounded-full">Siempre activa</span>
        ) : (
          <button
            onClick={() => onChange(!enabled)}
            className={`relative inline-flex w-9 h-5 items-center rounded-full transition-colors cursor-pointer flex-shrink-0 ${
              enabled ? "bg-amber-500" : "bg-stone-300"
            }`}
            role="switch"
            aria-checked={enabled}
          >
            <span
              className={`inline-block w-3.5 h-3.5 bg-white rounded-full transition-transform ${
                enabled ? "translate-x-4" : "translate-x-1"
              }`}
            />
          </button>
        )}
      </div>
      <p className="text-stone-500 text-xs leading-relaxed">{description}</p>
    </div>
  );
}
